import React, { useEffect, useState } from "react";
import { Wrapper } from "./style";
import DashFilterBar from "./DashFilterBar";
import DashAnalyticsTrendCard from "./DashAnalyticsTrendCard";
import DashTotalRevenueCard from "./DashTotalRevenueCard";
import DashRevenueMetricsStrip from "./DashRevenueMetricsStrip";
import DashUnitsNetProfitChartCard from "./DashUnitsNetProfitChartCard";
import DashCostBreakdownCard from "./DashCostBreakdownCard";
import DashProfitLossSidebarCard from "./DashProfitLossSidebarCard";
import useDashChartRows from "./useDashChartRows";
import { getAmount } from "../../../../utils/helper";
import ItemMaster from "../item-list";
import ChartSyncOverlay from "../../../../components/overlay/ChartSyncOverlay";
import { DashboardTableFilterProvider, useDashboardTableFilter } from "./DashboardTableFilterContext";
import { isSubUserDashboardAnalyticsEnabled } from "../../../../utils/subUser";
import useTaxInputCreditRecoverable from "../../../../hooks/useTaxInputCreditRecoverable";
import { getNetTaxTooltip } from "../../../../utils/netTaxTooltip";

/**
 * Dash layout: filters + analytics cards with sparklines & trends.
 */
export default function DashboardDash(props) {
  return (
    <DashboardTableFilterProvider>
      <DashboardDashInner {...props} />
    </DashboardTableFilterProvider>
  );
}

function DashboardDashInner({
  userData,
  selectedMarketplace,
  marketplaceList = [],
  isSpApiConnected = true,
  onMarketplaceChange,
  otherRange,
  onOtherRangeChange,
  isDemoDateUser = false,
  disabledDashboardDate,
  selectedAsins = [],
  setSelectedAsins,
  asinList = [],
  debouncedSearch,
  handleSearchBlur,
  handlePopupScroll,
  isSearching = false,
  hasMore = false,
  isDataSyncing = false,
  periodicity = "day",
  selectedSkus = [],
  selectedCurrencySign = "₹",
  /** Optimized chart APIs: `is_compare` + compare window (from filter bar). */
  dashCompareMeta,
  onDashCompareMetaChange,
  /** Optional JSON string e.g. `[{"attribute":"sales","operator":">","value":500}]` for both chart + summary. */
  dashFilterParams,
  activeView = "dashboard",
  productListFilters,
}) {
  const { filterParams: tableFilterParams } = useDashboardTableFilter();
  const effectiveFilterParams = tableFilterParams ?? dashFilterParams ?? null;
  const analyticsEnabled =
    isSpApiConnected || isSubUserDashboardAnalyticsEnabled(userData);
  const isTaxInputCreditRecoverable = useTaxInputCreditRecoverable(userData);
  const netTaxTooltip = getNetTaxTooltip(isTaxInputCreditRecoverable);

  const { loading, totals, trends, series, revenueKpis, costBreakdown, profitLoss } = useDashChartRows({
    userData,
    selectedMarketplace,
    otherRange,
    periodicity,
    selectedSkus,
    enabled: analyticsEnabled,
    compareMeta: dashCompareMeta,
    filterParams: effectiveFilterParams,
    isTaxInputCreditRecoverable,
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Render Products view
  if (activeView === "products") {
    return (
      <Wrapper className="">
        <div className="row g-5 g-xl-5 mb-5">
          <div className="col-lg-12">
            <div className="card border border-gray-200 shadow-none p-0 overflow-hidden">
              <div className="card-body p-3 p-md-4">
                <DashFilterBar
                  otherRange={otherRange}
                  onOtherRangeChange={onOtherRangeChange}
                  isDemoDateUser={isDemoDateUser}
                  disabledDate={disabledDashboardDate}
                  isSpApiConnected={isSpApiConnected}
                  selectedMarketplace={selectedMarketplace}
                  marketplaceList={marketplaceList}
                  onMarketplaceChange={onMarketplaceChange}
                  selectedAsins={selectedAsins}
                  setSelectedAsins={setSelectedAsins}
                  asinList={asinList}
                  debouncedSearch={debouncedSearch}
                  handleSearchBlur={handleSearchBlur}
                  handlePopupScroll={handlePopupScroll}
                  isSearching={isSearching}
                  hasMore={hasMore}
                  isDataSyncing={isDataSyncing}
                  onCompareMetaChange={onDashCompareMetaChange}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="row g-5 mb-5">
          <div className="col-md-12 mt-0 cus-product-list">
            <ItemMaster
              selectedMarketplace={selectedMarketplace}
              selectedCurrencySign={selectedCurrencySign}
              userData={userData}
              selectedTileData={productListFilters}
            />
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper className="">
      <div className="row g-5 g-xl-5 mb-5">
        <div className="col-lg-12">
          <div className="card border border-gray-200 shadow-none p-0 overflow-hidden">
            <div className="card-body p-3 p-md-4">
              <DashFilterBar
                otherRange={otherRange}
                onOtherRangeChange={onOtherRangeChange}
                isDemoDateUser={isDemoDateUser}
                disabledDate={disabledDashboardDate}
                isSpApiConnected={isSpApiConnected}
                selectedMarketplace={selectedMarketplace}
                marketplaceList={marketplaceList}
                onMarketplaceChange={onMarketplaceChange}
                selectedAsins={selectedAsins}
                setSelectedAsins={setSelectedAsins}
                asinList={asinList}
                debouncedSearch={debouncedSearch}
                handleSearchBlur={handleSearchBlur}
                handlePopupScroll={handlePopupScroll}
                isSearching={isSearching}
                hasMore={hasMore}
                isDataSyncing={isDataSyncing}
                onCompareMetaChange={onDashCompareMetaChange}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row g-5 g-xl-5 mb-5">
        <div className="col-lg-9 " style={{ position: "relative" }}>
          <ChartSyncOverlay visible={isDataSyncing} />
          <div className="row g-5 mb-5 analytics-cards-row">
            <div className="col-md-4">
              <DashTotalRevenueCard
                loading={loading}
                totals={totals}
                series={series}
                trend={trends.sales}
                ordersTotal={revenueKpis?.ordersTotal}
                unitsTotal={revenueKpis?.unitsTotalAgg}
                currencySign={selectedCurrencySign}
              />
            </div>
            <div className="col-md-8">
              <div className="row g-5">
                <div className="col-md-6 col-6 d-flex flex-column gap-5">
                  <DashAnalyticsTrendCard
                    title="Net Profit"
                    value={getAmount(totals.net, selectedCurrencySign)}
                    subtitle={`${totals.marginNet.toFixed(2)}% margin`}
                    trend={trends.net}
                    currencySign={selectedCurrencySign}
                    sparklineData={series.net}
                    sparkColor="#50cd8970"
                    loading={loading}
                    accentClass=""
                  />
                  <DashAnalyticsTrendCard
                    title="Ad Spend"
                    value={getAmount(totals.ad, selectedCurrencySign)}
                    subtitle={`TACoS ${totals.tacos.toFixed(1)}%`}
                    trend={trends.ad}
                    currencySign={selectedCurrencySign}
                    sparklineData={series.ad}
                    sparkColor="#f1416c70"
                    invertTrendColors
                    loading={loading}
                    accentClass="bg-light-warning"
                  />
                </div>
                <div className="col-md-6 col-6 d-flex flex-column gap-5">
                  <DashAnalyticsTrendCard
                    title="Gross Profit"
                    value={getAmount(totals.gross, selectedCurrencySign)}
                    subtitle={`${totals.marginGross.toFixed(2)}% margin`}
                    trend={trends.gross}
                    currencySign={selectedCurrencySign}
                    sparklineData={series.gross}
                    sparkColor="#7239ea70"
                    loading={loading}
                    accentClass=""
                  />
                  <DashAnalyticsTrendCard
                    title="Est. Payout"
                    titleTooltip="Estimated amount Amazon will transfer to your bank after all fees and deductions. This is a prediction and may differ from your final settlement."
                    value={getAmount(totals.payout, selectedCurrencySign)}
                    subtitle="Selected range"
                    trend={trends.payout}
                    currencySign={selectedCurrencySign}
                    sparklineData={series.payout}
                    sparkColor="#ffc70070"
                    loading={loading}
                    accentClass=""
                  />
                </div>
              </div>
            </div>
          </div>

          <DashRevenueMetricsStrip
            loading={loading}
            revenueKpis={revenueKpis}
            currencySign={selectedCurrencySign}
          />

          <div className="row g-5 mb-5">
            <div className="col-md-8 chart-card">
              <DashUnitsNetProfitChartCard
                loading={loading}
                series={series}
                totals={totals}
                currencySign={selectedCurrencySign}
                periodicity={periodicity}
                isMobile={isMobile}
              />
            </div>
            <div className="col-md-4">
              <DashCostBreakdownCard
                loading={loading}
                costBreakdown={costBreakdown}
                currencySign={selectedCurrencySign}
                netTaxTooltip={netTaxTooltip}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-3 side-bar-card" style={{ paddingBottom: "15px" }}>
          <DashProfitLossSidebarCard
            loading={loading}
            profitLoss={profitLoss}
            currencySign={selectedCurrencySign}
            netTaxTooltip={netTaxTooltip}
            isTaxInputCreditRecoverable={isTaxInputCreditRecoverable}
          />
        </div>
      </div>
      {!isMobile && (  
        <div className="row g-5 mb-5">
          <div className="col-md-12 mt-0 ">
            <ItemMaster
              selectedMarketplace={selectedMarketplace}
              selectedCurrencySign={selectedCurrencySign}
              userData={userData}
              selectedTileData={productListFilters}
              isMobile={isMobile}
            />
          </div>
        </div>
      )}

    </Wrapper>
  );
}
